/**
 * Pure helpers for the customer Webhooks section (endpoints, deliveries, retries).
 *
 * Everything here is side-effect free and covered 100% by
 * `__tests__/utils.test.ts`. Components and hooks hold no formatting or
 * request-building logic of their own — it lives here so it can be tested.
 *
 * HIPAA: webhook payloads carry compass ids only, but header values, secrets
 * and URLs are still customer credentials. Nothing here logs, and request
 * builders copy fields one by one (the server rejects unknown fields, and a
 * spread of a fetched endpoint would also echo read-only fields back).
 */
import type {
  CreateWebhookEndpointRequest,
  ListWebhookDeliveriesRequest,
  UpdateWebhookEndpointRequest,
  WebhookCustomHeaderInput,
  WebhookEndpoint,
  WebhookEndpointHealth,
  WebhookManualAction,
  WebhookOperation,
} from '@compass/client/types/webhook';
import {
  WebhookActorKind,
  WebhookAttemptOutcome,
  WebhookDeliveryStatus,
  WebhookEndpointStatus,
  WebhookEnvironment,
  WebhookManualActionKind,
  WebhookOperationState,
} from '@compass/client/types/webhook';

import type { SupportedTimeZone } from '@/constants/date';
import { settingsPath } from '@/pages/settings/settingsTabs';
import { naiveDateTimeToUtc } from '@/utils/date';
import { buildOrgRoutePath } from '@/utils/routes';

// ---------------------------------------------------------------------------
// Event catalog
// ---------------------------------------------------------------------------

/** Always delivered to every endpoint; never stored in `event_types`. */
export const WEBHOOK_TEST_EVENT_TYPE = 'webhook.test';

interface EventTypeOption {
  value: string;
  label: string;
  description: string;
}

/** The case events an endpoint can subscribe to (compass event catalog). */
export const CASE_EVENT_TYPES: readonly EventTypeOption[] = [
  {
    value: 'case.created.success',
    label: 'Case created',
    description: 'A case was created and its workflow started.',
  },
  {
    value: 'case.created.failed',
    label: 'Case creation failed',
    description: 'A case was created but its workflow could not be started.',
  },
  {
    value: 'case.completed',
    label: 'Case completed',
    description: 'A case was completed or cancelled.',
  },
  {
    value: 'case.terminated',
    label: 'Case terminated',
    description: 'A case was terminated.',
  },
];

/** Every event type that can appear on a delivery, for the Deliveries filter. */
export const DELIVERY_EVENT_TYPE_OPTIONS: { value: string; label: string }[] = [
  ...CASE_EVENT_TYPES.map(({ value, label }) => ({ value, label })),
  { value: WEBHOOK_TEST_EVENT_TYPE, label: 'Test event' },
];

export function eventTypeLabel(type: string | undefined): string {
  if (!type) return '—';
  return DELIVERY_EVENT_TYPE_OPTIONS.find(o => o.value === type)?.label ?? type;
}

// ---------------------------------------------------------------------------
// Enum labels and badge variants
// ---------------------------------------------------------------------------

type WebhookBadgeVariant = 'success' | 'progress' | 'destructive' | 'secondary' | 'warning';

export function environmentLabel(env: WebhookEnvironment | undefined): string {
  switch (env) {
    case WebhookEnvironment.WEBHOOK_ENVIRONMENT_PRODUCTION:
      return 'Production';
    case WebhookEnvironment.WEBHOOK_ENVIRONMENT_SANDBOX:
      return 'Developer';
    default:
      return '—';
  }
}

export function endpointStatusLabel(status: WebhookEndpointStatus | undefined): string {
  switch (status) {
    case WebhookEndpointStatus.WEBHOOK_ENDPOINT_STATUS_ACTIVE:
      return 'Active';
    case WebhookEndpointStatus.WEBHOOK_ENDPOINT_STATUS_PAUSED:
      return 'Paused';
    case WebhookEndpointStatus.WEBHOOK_ENDPOINT_STATUS_DISABLED:
      return 'Disabled';
    default:
      return 'Unknown';
  }
}

export function endpointStatusVariant(
  status: WebhookEndpointStatus | undefined,
): WebhookBadgeVariant {
  switch (status) {
    case WebhookEndpointStatus.WEBHOOK_ENDPOINT_STATUS_ACTIVE:
      return 'success';
    case WebhookEndpointStatus.WEBHOOK_ENDPOINT_STATUS_PAUSED:
      return 'warning';
    default:
      return 'secondary';
  }
}

/** Which status transitions the row menu / detail page offer for an endpoint. */
export function endpointStatusActions(status: WebhookEndpointStatus | undefined): {
  canPause: boolean;
  canResume: boolean;
  canDisable: boolean;
} {
  return {
    canPause: status === WebhookEndpointStatus.WEBHOOK_ENDPOINT_STATUS_ACTIVE,
    canResume:
      status === WebhookEndpointStatus.WEBHOOK_ENDPOINT_STATUS_PAUSED ||
      status === WebhookEndpointStatus.WEBHOOK_ENDPOINT_STATUS_DISABLED,
    canDisable: status !== WebhookEndpointStatus.WEBHOOK_ENDPOINT_STATUS_DISABLED,
  };
}

/** Retry and test events are refused by the server for a non-active endpoint. */
export function isEndpointActive(endpoint: Pick<WebhookEndpoint, 'status'> | undefined): boolean {
  return endpoint?.status === WebhookEndpointStatus.WEBHOOK_ENDPOINT_STATUS_ACTIVE;
}

// ---------------------------------------------------------------------------
// Managed endpoints (set up by 100ms support)
// ---------------------------------------------------------------------------

export const MANAGED_ENDPOINT_LABEL = 'Managed by 100ms';
export const MANAGED_ENDPOINT_HINT =
  'This endpoint is managed by 100ms support. Contact 100ms support to change it.';

/**
 * An endpoint 100ms support set up for the organization (the server sets
 * `managed` when its filter has a source client or a filter organization other
 * than the owner). Customers can view it, list
 * its deliveries and retry them; everything else is refused by the server.
 */
export function isEndpointManaged(endpoint: Pick<WebhookEndpoint, 'managed'> | undefined): boolean {
  return endpoint?.managed === true;
}

/**
 * Endpoint actions the server refuses on a managed endpoint (FailedPrecondition
 * "endpoint is managed by 100ms support"). Viewing deliveries and retrying
 * them stay allowed.
 */
const MANAGED_REFUSED_ACTIONS: ReadonlySet<string> = new Set([
  'edit',
  'delete',
  'pause',
  'resume',
  'disable',
  'rotate',
  'test',
]);

/** False when `action` would be refused because the endpoint is managed by 100ms. */
export function isActionAllowedOnEndpoint(
  action: string,
  endpoint: Pick<WebhookEndpoint, 'managed'> | undefined,
): boolean {
  return !isEndpointManaged(endpoint) || !MANAGED_REFUSED_ACTIONS.has(action);
}

export function deliveryStatusLabel(status: WebhookDeliveryStatus | undefined): string {
  switch (status) {
    case WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_PENDING:
      return 'Pending';
    case WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_RETRYING:
      return 'Retrying';
    case WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_SUCCEEDED:
      return 'Succeeded';
    case WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_EXHAUSTED:
      return 'Exhausted';
    case WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_CANCELLED:
      return 'Cancelled';
    default:
      return 'Unknown';
  }
}

export function deliveryStatusVariant(
  status: WebhookDeliveryStatus | undefined,
): WebhookBadgeVariant {
  switch (status) {
    case WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_SUCCEEDED:
      return 'success';
    case WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_PENDING:
    case WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_RETRYING:
      return 'progress';
    case WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_EXHAUSTED:
      return 'destructive';
    default:
      return 'secondary';
  }
}

/** Options for the Deliveries status filter; values are the numeric enum as strings. */
export const DELIVERY_STATUS_OPTIONS: { value: string; label: string }[] = [
  WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_PENDING,
  WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_RETRYING,
  WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_SUCCEEDED,
  WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_EXHAUSTED,
  WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_CANCELLED,
].map(status => ({ value: String(status), label: deliveryStatusLabel(status) }));

const ATTEMPT_OUTCOME_LABELS: Record<WebhookAttemptOutcome, string> = {
  [WebhookAttemptOutcome.WEBHOOK_ATTEMPT_OUTCOME_UNSPECIFIED]: 'Unknown',
  [WebhookAttemptOutcome.WEBHOOK_ATTEMPT_OUTCOME_SUCCESS]: 'Success',
  [WebhookAttemptOutcome.WEBHOOK_ATTEMPT_OUTCOME_CLIENT_ERROR]: 'Client error (4xx)',
  [WebhookAttemptOutcome.WEBHOOK_ATTEMPT_OUTCOME_SERVER_ERROR]: 'Server error (5xx)',
  [WebhookAttemptOutcome.WEBHOOK_ATTEMPT_OUTCOME_TIMEOUT]: 'Timed out',
  [WebhookAttemptOutcome.WEBHOOK_ATTEMPT_OUTCOME_CONNECTION_ERROR]: 'Connection error',
  [WebhookAttemptOutcome.WEBHOOK_ATTEMPT_OUTCOME_TLS_ERROR]: 'TLS error',
  [WebhookAttemptOutcome.WEBHOOK_ATTEMPT_OUTCOME_REDIRECT]: 'Redirect (not followed)',
  [WebhookAttemptOutcome.WEBHOOK_ATTEMPT_OUTCOME_BLOCKED_ADDRESS]: 'Blocked address',
};

export function attemptOutcomeLabel(outcome: WebhookAttemptOutcome | undefined): string {
  return (
    ATTEMPT_OUTCOME_LABELS[outcome ?? WebhookAttemptOutcome.WEBHOOK_ATTEMPT_OUTCOME_UNSPECIFIED] ??
    'Unknown'
  );
}

export function isSuccessfulOutcome(outcome: WebhookAttemptOutcome | undefined): boolean {
  return outcome === WebhookAttemptOutcome.WEBHOOK_ATTEMPT_OUTCOME_SUCCESS;
}

const MANUAL_ACTION_LABELS: Record<WebhookManualActionKind, string> = {
  [WebhookManualActionKind.WEBHOOK_MANUAL_ACTION_KIND_UNSPECIFIED]: 'Action',
  [WebhookManualActionKind.WEBHOOK_MANUAL_ACTION_KIND_RETRY]: 'Retry',
  [WebhookManualActionKind.WEBHOOK_MANUAL_ACTION_KIND_RESEND]: 'Resend',
  [WebhookManualActionKind.WEBHOOK_MANUAL_ACTION_KIND_BULK_RETRY]: 'Bulk retry',
  [WebhookManualActionKind.WEBHOOK_MANUAL_ACTION_KIND_REPLAY]: 'Replay',
  [WebhookManualActionKind.WEBHOOK_MANUAL_ACTION_KIND_CANCEL]: 'Cancel',
};

export function manualActionLabel(kind: WebhookManualActionKind | undefined): string {
  return (
    MANUAL_ACTION_LABELS[kind ?? WebhookManualActionKind.WEBHOOK_MANUAL_ACTION_KIND_UNSPECIFIED] ??
    'Action'
  );
}

/** Who performed a manual action. Staff emails are blank in org scope by design. */
export function manualActionActor(
  action: Pick<WebhookManualAction, 'actor_kind' | 'actor_email'>,
): string {
  switch (action.actor_kind) {
    case WebhookActorKind.WEBHOOK_ACTOR_KIND_CUSTOMER:
      return action.actor_email || 'A member of your organization';
    case WebhookActorKind.WEBHOOK_ACTOR_KIND_STAFF:
      return '100ms support';
    case WebhookActorKind.WEBHOOK_ACTOR_KIND_SYSTEM:
      return 'System';
    default:
      return 'Unknown';
  }
}

export function operationStateLabel(state: WebhookOperationState | undefined): string {
  switch (state) {
    case WebhookOperationState.WEBHOOK_OPERATION_STATE_RUNNING:
      return 'Running';
    case WebhookOperationState.WEBHOOK_OPERATION_STATE_DONE:
      return 'Done';
    case WebhookOperationState.WEBHOOK_OPERATION_STATE_FAILED:
      return 'Failed';
    default:
      return 'Starting';
  }
}

// ---------------------------------------------------------------------------
// Retry eligibility
// ---------------------------------------------------------------------------

/**
 * The per-delivery action the drawer offers. Retrying/exhausted → Retry now
 * (fresh set of retries, same event id); succeeded → Resend; pending and
 * cancelled have none (a pending delivery is still in flight; a cancelled one
 * was stopped by a pause or disable, and what was missed while paused is
 * resent when the endpoint resumes).
 */
type DeliveryRetryAction = 'retry' | 'resend' | null;

export function deliveryRetryAction(
  status: WebhookDeliveryStatus | undefined,
): DeliveryRetryAction {
  switch (status) {
    case WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_RETRYING:
    case WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_EXHAUSTED:
      return 'retry';
    case WebhookDeliveryStatus.WEBHOOK_DELIVERY_STATUS_SUCCEEDED:
      return 'resend';
    default:
      return null;
  }
}

export function deliveryRetryActionLabel(action: Exclude<DeliveryRetryAction, null>): string {
  return action === 'retry' ? 'Retry now' : 'Resend';
}

/** Bulk retry only restarts retrying and exhausted deliveries (server rule). */
export function isBulkRetryable(status: WebhookDeliveryStatus | undefined): boolean {
  return deliveryRetryAction(status) === 'retry';
}

/**
 * Client cap on delivery_ids in one BulkRetry call (a page holds at most 100;
 * the server accepts far more).
 */
export const MAX_BULK_RETRY_IDS = 1000;

// ---------------------------------------------------------------------------
// Validation (mirrors iris pkg/webhook; the server stays authoritative)
// ---------------------------------------------------------------------------

const MAX_URL_LENGTH = 2048;
const MAX_HEADER_NAME_LENGTH = 128;
const MAX_HEADER_VALUE_BYTES = 8192;
const MAX_NAME_LENGTH = 100;
/** Server cap on custom headers per endpoint. */
export const MAX_CUSTOM_HEADERS = 20;

export const MIN_TIMEOUT_SECONDS = 1;
export const MAX_TIMEOUT_SECONDS = 60;
export const DEFAULT_TIMEOUT_SECONDS = 30;

/** Returns an error message, or null when the URL is acceptable. */
export function validateEndpointUrl(raw: string): string | null {
  const value = raw.trim();
  if (!value) return 'Enter the URL that should receive events.';
  if (value.length > MAX_URL_LENGTH) return `The URL must be at most ${MAX_URL_LENGTH} characters.`;
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return 'Enter a valid URL, e.g. https://example.com/webhooks.';
  }
  if (parsed.protocol !== 'https:') return 'The URL must use https://.';
  // The browser's URL parser repairs "https:host" and "https:\\host"; the server
  // (Go net/url) reads them as opaque URLs without a host and refuses them.
  if (!/^https:\/\//i.test(value)) {
    return 'Enter a valid URL, e.g. https://example.com/webhooks.';
  }
  if (parsed.username || parsed.password) return 'The URL must not contain a username or password.';
  const host = parsed.hostname.toLowerCase();
  if (host === 'localhost' || host.endsWith('.localhost')) {
    return 'The URL must be reachable from the internet.';
  }
  return null;
}

export function validateEndpointName(raw: string): string | null {
  const value = raw.trim();
  if (!value) return 'Enter a name.';
  if (value.length > MAX_NAME_LENGTH)
    return `The name must be at most ${MAX_NAME_LENGTH} characters.`;
  return null;
}

/** Whole seconds between 1 and 60. */
export function validateTimeoutSeconds(value: number): string | null {
  if (!Number.isInteger(value) || value < MIN_TIMEOUT_SECONDS || value > MAX_TIMEOUT_SECONDS) {
    return `Timeout must be a whole number of seconds from ${MIN_TIMEOUT_SECONDS} to ${MAX_TIMEOUT_SECONDS}.`;
  }
  return null;
}

/** Header names customers may not set (compared lowercased, `_` read as `-`). */
const RESERVED_HEADER_NAMES = new Set([
  'host',
  'content-length',
  'transfer-encoding',
  'trailer',
  'te',
  'connection',
  'keep-alive',
  'proxy-connection',
  'upgrade',
]);
const RESERVED_HEADER_PREFIX = 'webhook-';
const HEADER_TOKEN = /^[A-Za-z0-9!#$%&'*+\-.^_`|~]+$/;

export function normalizeHeaderName(name: string): string {
  return name.trim().toLowerCase().replace(/_/g, '-');
}

export function validateHeaderName(raw: string): string | null {
  const name = raw.trim();
  if (!name) return 'Enter a header name.';
  if (name.length > MAX_HEADER_NAME_LENGTH) {
    return `Header names must be at most ${MAX_HEADER_NAME_LENGTH} characters.`;
  }
  if (!HEADER_TOKEN.test(name)) {
    return "Header names may use letters, digits and !#$%&'*+-.^_`|~ only.";
  }
  const norm = normalizeHeaderName(name);
  if (RESERVED_HEADER_NAMES.has(norm) || norm.startsWith(RESERVED_HEADER_PREFIX)) {
    return `${name} is reserved and cannot be set.`;
  }
  return null;
}

function utf8ByteLength(value: string): number {
  let bytes = 0;
  for (const ch of value) {
    const code = ch.codePointAt(0) as number;
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code < 0x10000) bytes += 3;
    else bytes += 4;
  }
  return bytes;
}

export function validateHeaderValue(value: string): string | null {
  if (utf8ByteLength(value) > MAX_HEADER_VALUE_BYTES) return 'Header values must be at most 8 KB.';
  for (let i = 0; i < value.length; i += 1) {
    const c = value.charCodeAt(i);
    if ((c < 0x20 && c !== 0x09) || c === 0x7f)
      return 'Header values cannot contain control characters.';
  }
  return null;
}

/**
 * One row of the custom-headers editor. `existing` rows came from the stored
 * endpoint: their value is never returned, so an empty value means "keep the
 * stored value" (the request omits `value`). New rows always need a value.
 */
export interface HeaderRow {
  key: string;
  name: string;
  value: string;
  existing: boolean;
}

/** Per-row error messages, keyed by row key; empty when every row is valid. */
export function validateHeaderRows(rows: HeaderRow[]): Record<string, string> {
  const errors: Record<string, string> = {};
  const seen = new Set<string>();
  rows.forEach(row => {
    const nameError = validateHeaderName(row.name);
    if (nameError) {
      errors[row.key] = nameError;
      return;
    }
    const norm = normalizeHeaderName(row.name);
    if (seen.has(norm)) {
      errors[row.key] = `${row.name.trim()} is listed more than once.`;
      return;
    }
    seen.add(norm);
    if (!row.existing && row.value === '') {
      errors[row.key] = 'Enter a value for the new header.';
      return;
    }
    const valueError = validateHeaderValue(row.value);
    if (valueError) errors[row.key] = valueError;
  });
  return errors;
}

/** Request headers: existing rows left blank omit `value`, which keeps the stored one. */
export function buildHeaderInputs(rows: HeaderRow[]): WebhookCustomHeaderInput[] {
  return rows.map(row => {
    const name = row.name.trim();
    if (row.existing && row.value === '') return { name };
    return { name, value: row.value };
  });
}

// ---------------------------------------------------------------------------
// Endpoint form <-> requests
// ---------------------------------------------------------------------------

export interface EndpointFormState {
  name: string;
  description: string;
  url: string;
  environment: WebhookEnvironment;
  eventTypes: string[];
  programIds: string[];
  headers: HeaderRow[];
  timeoutSeconds: number;
  omitReferenceId: boolean;
}

let headerRowSeq = 0;
/** A unique key for a new header row (React key + error map key). */
export function newHeaderRowKey(): string {
  headerRowSeq += 1;
  return `header-${headerRowSeq}`;
}

export function emptyEndpointForm(): EndpointFormState {
  return {
    name: '',
    description: '',
    url: '',
    environment: WebhookEnvironment.WEBHOOK_ENVIRONMENT_PRODUCTION,
    eventTypes: CASE_EVENT_TYPES.map(e => e.value),
    programIds: [],
    headers: [],
    timeoutSeconds: DEFAULT_TIMEOUT_SECONDS,
    omitReferenceId: false,
  };
}

function headerRowsFromEndpoint(endpoint: WebhookEndpoint): HeaderRow[] {
  return (endpoint.custom_headers ?? []).map(h => ({
    key: newHeaderRowKey(),
    name: h.name ?? '',
    value: '',
    existing: true,
  }));
}

/** Seed the edit form from a stored endpoint. Header values are write-only, so start blank. */
export function endpointToForm(endpoint: WebhookEndpoint): EndpointFormState {
  const { name = '', description = '', url = '', omit_reference_id = false } = endpoint;
  return {
    name,
    description,
    url,
    environment: endpoint.environment || WebhookEnvironment.WEBHOOK_ENVIRONMENT_PRODUCTION,
    eventTypes: (endpoint.event_types ?? []).filter(t => t !== WEBHOOK_TEST_EVENT_TYPE),
    programIds: [...(endpoint.filter?.program_ids ?? [])],
    headers: headerRowsFromEndpoint(endpoint),
    timeoutSeconds: endpoint.timeout_seconds || DEFAULT_TIMEOUT_SECONDS,
    omitReferenceId: omit_reference_id,
  };
}

/** Every field error in the form, keyed by field ("headers" holds the per-row map). */
export interface EndpointFormErrors {
  name?: string;
  url?: string;
  eventTypes?: string;
  timeoutSeconds?: string;
  headers?: Record<string, string>;
}

export function validateEndpointForm(form: EndpointFormState): EndpointFormErrors {
  const errors: EndpointFormErrors = {};
  const nameError = validateEndpointName(form.name);
  if (nameError) errors.name = nameError;
  const urlError = validateEndpointUrl(form.url);
  if (urlError) errors.url = urlError;
  if (form.eventTypes.length === 0) errors.eventTypes = 'Subscribe to at least one case event.';
  const timeoutError = validateTimeoutSeconds(form.timeoutSeconds);
  if (timeoutError) errors.timeoutSeconds = timeoutError;
  const headerErrors = validateHeaderRows(form.headers);
  if (Object.keys(headerErrors).length > 0) errors.headers = headerErrors;
  return errors;
}

export function hasFormErrors(errors: EndpointFormErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** Keep catalog order and drop anything not in the catalog (webhook.test included). */
function orderedEventTypes(selected: string[]): string[] {
  return CASE_EVENT_TYPES.map(e => e.value).filter(v => selected.includes(v));
}

export function buildCreateEndpointRequest(
  organizationId: string,
  form: EndpointFormState,
): CreateWebhookEndpointRequest {
  return {
    organization_id: organizationId,
    name: form.name.trim(),
    description: form.description.trim(),
    environment: form.environment,
    url: form.url.trim(),
    event_types: orderedEventTypes(form.eventTypes),
    program_ids: [...form.programIds],
    workflow_types: [],
    custom_headers: buildHeaderInputs(form.headers),
    timeout_seconds: form.timeoutSeconds,
    omit_reference_id: form.omitReferenceId,
  };
}

/**
 * Update is a full replace of the editable fields, so fields this form does
 * not edit (workflow_types) are carried over from the stored endpoint, and
 * `version` is sent for optimistic locking (a stale version is a 409).
 */
export function buildUpdateEndpointRequest(
  organizationId: string,
  endpoint: WebhookEndpoint,
  form: EndpointFormState,
): UpdateWebhookEndpointRequest {
  return {
    organization_id: organizationId,
    endpoint_id: endpoint.id,
    version: endpoint.version ?? 0,
    name: form.name.trim(),
    description: form.description.trim(),
    environment: form.environment,
    url: form.url.trim(),
    event_types: orderedEventTypes(form.eventTypes),
    program_ids: [...form.programIds],
    workflow_types: [...(endpoint.filter?.workflow_types ?? [])],
    custom_headers: buildHeaderInputs(form.headers),
    timeout_seconds: form.timeoutSeconds,
    omit_reference_id: form.omitReferenceId,
  };
}

function programsSummary(ids: string[]): string {
  if (ids.length === 0) return 'All programs';
  return ids.length === 1 ? '1 program' : `${ids.length} programs`;
}

function sameSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every(v => b.includes(v));
}

function fieldChanges(before: EndpointFormState, form: EndpointFormState): string[] {
  const changes: string[] = [];
  const name = form.name.trim();
  if (name !== before.name) changes.push(`Name: ${before.name || EMPTY_VALUE} → ${name}`);
  if (form.description.trim() !== before.description.trim()) changes.push('Description updated');
  if (form.url.trim() !== before.url) changes.push(`URL: ${form.url.trim()}`);
  if (form.environment !== before.environment) {
    changes.push(
      `Environment: ${environmentLabel(before.environment)} → ${environmentLabel(form.environment)}`,
    );
  }
  return changes;
}

function eventAndProgramChanges(before: EndpointFormState, form: EndpointFormState): string[] {
  const changes: string[] = [];
  const added = orderedEventTypes(form.eventTypes).filter(t => !before.eventTypes.includes(t));
  const removed = orderedEventTypes(before.eventTypes).filter(t => !form.eventTypes.includes(t));
  if (added.length > 0) changes.push(`Events added: ${added.join(', ')}`);
  if (removed.length > 0) changes.push(`Events removed: ${removed.join(', ')}`);
  if (!sameSet(form.programIds, before.programIds)) {
    changes.push(
      `Programs: ${programsSummary(before.programIds)} → ${programsSummary(form.programIds)}`,
    );
  }
  return changes;
}

/** Header names only: stored values are write-only and never shown. */
function headerChanges(before: EndpointFormState, form: EndpointFormState): string[] {
  const storedNames = before.headers.map(h => h.name.trim()).filter(Boolean);
  const formNames = form.headers.map(h => h.name.trim()).filter(Boolean);
  const replaced = form.headers
    .filter(h => h.value !== '' && storedNames.includes(h.name.trim()))
    .map(h => h.name.trim());
  const groups: [string, string[]][] = [
    ['Headers added', formNames.filter(n => !storedNames.includes(n))],
    ['Headers removed', storedNames.filter(n => !formNames.includes(n))],
    ['Header values replaced', replaced],
  ];
  return groups
    .filter(([, names]) => names.length > 0)
    .map(([label, names]) => `${label}: ${names.join(', ')}`);
}

function deliveryChanges(before: EndpointFormState, form: EndpointFormState): string[] {
  const changes: string[] = [];
  if (form.timeoutSeconds !== before.timeoutSeconds) {
    changes.push(`Timeout: ${before.timeoutSeconds} s → ${form.timeoutSeconds} s`);
  }
  if (form.omitReferenceId !== before.omitReferenceId) {
    changes.push(
      form.omitReferenceId
        ? 'referenceId: left out of payloads'
        : 'referenceId: included in payloads',
    );
  }
  return changes;
}

/**
 * One line per field an edit changes, for the Save changes confirmation, in
 * form order. Empty when nothing changed. Header values are write-only, so a
 * replaced value is reported by header name only.
 */
export function describeEndpointChanges(
  endpoint: WebhookEndpoint,
  form: EndpointFormState,
): string[] {
  const before = endpointToForm(endpoint);
  return [
    ...fieldChanges(before, form),
    ...eventAndProgramChanges(before, form),
    ...headerChanges(before, form),
    ...deliveryChanges(before, form),
  ];
}

// ---------------------------------------------------------------------------
// Health formatting
// ---------------------------------------------------------------------------

export const EMPTY_VALUE = '—';

/** success_rate is 0..1 and unset when there were no attempts. */
export function formatSuccessRate(rate: number | undefined | null): string {
  if (rate === undefined || rate === null || Number.isNaN(rate)) return EMPTY_VALUE;
  // One decimal, without a trailing ".0" (50%, 99.9%, 100%).
  return `${(rate * 100).toFixed(1).replace(/\.0$/, '')}%`;
}

export function successRateTone(rate: number | undefined | null): 'good' | 'warn' | 'bad' | 'none' {
  if (rate === undefined || rate === null || Number.isNaN(rate)) return 'none';
  if (rate >= 0.99) return 'good';
  if (rate >= 0.9) return 'warn';
  return 'bad';
}

export function formatLatencyMs(ms: number | undefined | null): string {
  if (!ms) return EMPTY_VALUE;
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(ms < 10_000 ? 2 : 1)} s`;
}

/** Deliveries waiting on the endpoint: still retrying plus exhausted. */
export function backlogCount(health: WebhookEndpointHealth | undefined): number {
  return (health?.retrying ?? 0) + (health?.exhausted ?? 0);
}

export function healthByEndpointId(
  list: WebhookEndpointHealth[] | undefined,
): Map<string, WebhookEndpointHealth> {
  const map = new Map<string, WebhookEndpointHealth>();
  (list ?? []).forEach(h => {
    if (h.endpoint_id) map.set(h.endpoint_id, h);
  });
  return map;
}

/** GetWebhookEndpointHealth accepts 1 to 100 ids per call. */
export const MAX_HEALTH_ENDPOINT_IDS = 100;

export function chunkIds(ids: string[], size: number): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));
  return chunks;
}

// ---------------------------------------------------------------------------
// Secrets
// ---------------------------------------------------------------------------

/** The server already masks the hint (`whsec_…abcd`); never derive one from a secret. */
export function secretHintLabel(hint: string | undefined): string {
  return hint || 'whsec_…';
}

// ---------------------------------------------------------------------------
// Deliveries filters / requests / paging
// ---------------------------------------------------------------------------

export type DeliverySearchType = 'case_id' | 'reference_id';

export const DELIVERY_SEARCH_OPTIONS: { key: DeliverySearchType; label: string }[] = [
  { key: 'case_id', label: 'Case ID' },
  { key: 'reference_id', label: 'Reference ID' },
];

export interface DeliveryFilterState {
  searchType: DeliverySearchType;
  searchValue: string;
  endpointIds: string[];
  /** Numeric WebhookDeliveryStatus values as strings (select option values). */
  statuses: string[];
  eventTypes: string[];
  /**
   * Naive `YYYY-MM-DDTHH:mm` bounds in the viewer's timezone, both inclusive
   * to the minute (a bare `YYYY-MM-DD` means the whole day); empty means
   * unbounded.
   */
  dateFrom: string;
  dateTo: string;
}

export function emptyDeliveryFilters(endpointId?: string): DeliveryFilterState {
  return {
    searchType: 'case_id',
    searchValue: '',
    endpointIds: endpointId ? [endpointId] : [],
    statuses: [],
    eventTypes: [],
    dateFrom: '',
    dateTo: '',
  };
}

export function hasActiveDeliveryFilters(filters: DeliveryFilterState): boolean {
  return (
    filters.searchValue.trim() !== '' ||
    filters.endpointIds.length > 0 ||
    filters.statuses.length > 0 ||
    filters.eventTypes.length > 0 ||
    filters.dateFrom !== '' ||
    filters.dateTo !== ''
  );
}

export const DELIVERIES_PAGE_SIZE = 50;

export function buildListDeliveriesRequest(
  organizationId: string,
  filters: DeliveryFilterState,
  timeZone: SupportedTimeZone,
  cursor: string | undefined,
  limit: number = DELIVERIES_PAGE_SIZE,
): ListWebhookDeliveriesRequest {
  const request: ListWebhookDeliveriesRequest = { organization_id: organizationId, limit };
  const search = filters.searchValue.trim();
  if (search) {
    if (filters.searchType === 'case_id') request.case_id = search;
    else request.reference_id = search;
  }
  if (filters.endpointIds.length > 0) request.endpoint_ids = [...filters.endpointIds];
  const statuses = filters.statuses
    .map(s => Number(s))
    .filter(s => Number.isInteger(s) && s > 0) as WebhookDeliveryStatus[];
  if (statuses.length > 0) request.statuses = statuses;
  if (filters.eventTypes.length > 0) request.event_types = [...filters.eventTypes];
  if (filters.dateFrom)
    request.occurred_after = naiveDateTimeToUtc(filters.dateFrom, timeZone, 'start');
  if (filters.dateTo)
    request.occurred_before = naiveDateTimeToUtc(filters.dateTo, timeZone, 'end-exclusive');
  if (cursor) request.start = cursor;
  return request;
}

/**
 * Cursor stack for opaque-cursor paging: page 1 has no cursor; going to page
 * N+1 records the next_cursor that page N returned. Returns a new record.
 */
export function rememberNextCursor(
  cursors: Record<number, string | undefined>,
  page: number,
  nextCursor: string | undefined,
): Record<number, string | undefined> {
  if (!nextCursor || cursors[page + 1] === nextCursor) return cursors;
  return { ...cursors, [page + 1]: nextCursor };
}

// ---------------------------------------------------------------------------
// Typed confirmations / operations
// ---------------------------------------------------------------------------

export const DELETE_CONFIRM_WORD = 'DELETE';

export function isTypedConfirmation(input: string, word: string): boolean {
  return input.trim() === word;
}

export function isOperationRunning(op: Pick<WebhookOperation, 'state'> | undefined): boolean {
  return (
    !op ||
    op.state === undefined ||
    op.state === WebhookOperationState.WEBHOOK_OPERATION_STATE_UNSPECIFIED ||
    op.state === WebhookOperationState.WEBHOOK_OPERATION_STATE_RUNNING
  );
}

/** Percent of selected deliveries already handled (started or skipped), 0..100. */
export function operationProgressPercent(op: WebhookOperation | undefined): number {
  const total = op?.total ?? 0;
  if (total <= 0) return op?.state === WebhookOperationState.WEBHOOK_OPERATION_STATE_DONE ? 100 : 0;
  const handled = (op?.started ?? 0) + (op?.skipped ?? 0);
  return Math.min(100, Math.round((handled / total) * 100));
}

/** Poll every 2 s while an operation runs, stop once it is done or failed. */
export const OPERATION_POLL_INTERVAL_MS = 2000;

export function operationRefetchInterval(op: WebhookOperation | undefined): number | false {
  return isOperationRunning(op) ? OPERATION_POLL_INTERVAL_MS : false;
}

/** Live mode on the delivery log re-reads the first page (and the open drawer) this often. */
export const LIVE_POLL_INTERVAL_MS = 5000;

/**
 * True when the filter's end bound has already passed, so nothing new can
 * match it and Live has nothing to follow. An empty or future end stays open.
 */
export function isDeliveryRangeClosed(
  dateTo: string,
  timeZone: SupportedTimeZone,
  now: number = Date.now(),
): boolean {
  if (!dateTo) return false;
  const end = Date.parse(naiveDateTimeToUtc(dateTo, timeZone, 'end-exclusive'));
  return Number.isFinite(end) && end <= now;
}

// ---------------------------------------------------------------------------
// Event JSON
// ---------------------------------------------------------------------------

/** Pretty-print the exact event JSON for display; fall back to the raw text. */
export function prettyEventJson(raw: string | undefined): string {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

interface HttpLikeError {
  response?: { status?: number; data?: { error?: unknown; message?: unknown } };
}

export function httpStatusOf(err: unknown): number | undefined {
  return (err as HttpLikeError | null | undefined)?.response?.status;
}

/** Stale `version` on update (gRPC Aborted → HTTP 409). */
export function isConflictError(err: unknown): boolean {
  return httpStatusOf(err) === 409;
}

/** The delivery dispatcher is down or unconfigured (gRPC Unavailable → HTTP 503). */
export function isUnavailableError(err: unknown): boolean {
  return httpStatusOf(err) === 503;
}

/**
 * A user-facing message: the server's own message for client errors (compass
 * writes `{"error": "..."}`), fixed text for 5xx and network errors.
 */
export function webhookErrorMessage(err: unknown, fallback: string): string {
  const status = httpStatusOf(err);
  if (status === 503) {
    return 'Webhook deliveries are temporarily unavailable. Endpoint settings still work.';
  }
  const serverMessage = serverMessageOf(err);
  if (status !== undefined && status >= 400 && status < 500 && serverMessage) return serverMessage;
  return fallback;
}

function serverMessageOf(err: unknown): string {
  const data = (err as HttpLikeError | null | undefined)?.response?.data;
  if (typeof data?.error === 'string' && data.error) return data.error;
  if (typeof data?.message === 'string' && data.message) return data.message;
  return '';
}

// ---------------------------------------------------------------------------
// Routing
// ---------------------------------------------------------------------------

export type WebhooksTab = 'endpoints' | 'deliveries';

export function parseWebhooksTab(value: string | null | undefined): WebhooksTab {
  return value === 'deliveries' ? 'deliveries' : 'endpoints';
}

export function webhooksPath(
  organizationId: string,
  options: { tab?: WebhooksTab; endpointId?: string; deliveryId?: string } = {},
): string {
  const params = new URLSearchParams();
  if (options.tab && options.tab !== 'endpoints') params.set('tab', options.tab);
  if (options.endpointId) params.set('endpoint', options.endpointId);
  if (options.deliveryId) params.set('delivery', options.deliveryId);
  const query = params.toString();
  return `${buildOrgRoutePath('/webhooks', organizationId)}${query ? `?${query}` : ''}`;
}

export function endpointDetailPath(organizationId: string, endpointId: string): string {
  return buildOrgRoutePath(`/webhooks/endpoints/${encodeURIComponent(endpointId)}`, organizationId);
}

/**
 * Settings → Developer, where the Webhooks pages are opened from. Settings is
 * program-scoped; without a selected program there is no such page, so the
 * breadcrumb falls back to plain text (undefined).
 */
export function developerSettingsPath(
  organizationId: string,
  programId: string | null | undefined,
): string | undefined {
  return organizationId && programId
    ? settingsPath(organizationId, programId, 'developer')
    : undefined;
}

/** The feature flag that turns the section on for one organization. */
export function webhooksFeatureFlagKey(
  organizationId: string | null | undefined,
): string | undefined {
  return organizationId ? `webhooks_${organizationId}` : undefined;
}
